import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/page-header";
import {
  OSImageForm,
  OS_IMAGE_FORM_ID,
} from "@/components/admin/os-images/OSImageForm";
import {
  useAdminOSImageDetail,
  useUpdateOSImage,
} from "@/hooks/useAdminOSImageQueries";
import type { OSImageFormData } from "@/schemas/adminOSImage.schema";

// No back button in the header: the breadcrumb already links to the list, and Cancel goes there
// too. Cancel and Save sit on the title row, so they stay in reach without scrolling the form.
export function EditOSImagePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: osImage, isLoading, error } = useAdminOSImageDetail(id!);
  const updateOSImage = useUpdateOSImage();

  if (!id) {
    return (
      <div className="space-y-6">
        <div className="py-12 text-center">
          <p className="text-destructive-ink">Invalid OS image ID</p>
          <Button onClick={() => navigate("/admin/os-images")} className="mt-4">
            Back to OS Images
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Edit OS Image" />
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">
                Loading OS image...
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !osImage) {
    return (
      <div className="space-y-6">
        <PageHeader title="Edit OS Image" />
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-destructive-ink">Failed to load OS image</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {error?.message || "OS image not found"}
              </p>
              <Button
                onClick={() => navigate("/admin/os-images")}
                className="mt-4"
              >
                Back to OS Images
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = (data: OSImageFormData) => {
    updateOSImage.mutate(
      { id: id!, values: data, current: osImage },
      {
        onSuccess: () => {
          navigate("/admin/os-images");
        },
      },
    );
  };

  // "Windows Server 2022" + "2022" would read as "Windows Server 2022 2022".
  const imageLabel =
    osImage.version && !osImage.name.includes(osImage.version)
      ? `${osImage.name} ${osImage.version}`
      : osImage.name;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit OS Image"
        subtitle={imageLabel}
        leading={
          osImage.iconUrl ? (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border bg-white p-2 dark:bg-white">
              <img
                src={osImage.iconUrl}
                alt=""
                className="h-full w-full object-contain"
              />
            </div>
          ) : undefined
        }
        action={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/os-images")}
              disabled={updateOSImage.isPending}
            >
              Cancel
            </Button>
            {/* Outside the <form>; `form` points at its id so this still submits it. */}
            <Button
              type="submit"
              form={OS_IMAGE_FORM_ID}
              disabled={updateOSImage.isPending}
            >
              {updateOSImage.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update OS Image
            </Button>
          </>
        }
      />

      <OSImageForm image={osImage} onSubmit={handleSubmit} />
    </div>
  );
}
